import { format } from 'date-fns';
import { fr } from 'date-fns/locale';
import {
  AlertCircle,
  Archive,
  Bell, BellOff,
  Bookmark,
  Check,
  CheckCheck,
  ChevronLeft,
  ChevronRight,
  Clock,
  Download,
  Eye,
  FileText,
  Filter,
  Key,
  Lock,
  Mail,
  MessageCircle,
  MoreVertical,
  Paperclip,
  Plus,
  Reply,
  RotateCw,
  Search,
  Send,
  Shield,
  Smartphone,
  Star,
  Tag,
  Trash2,
  Upload,
  X
} from 'lucide-react';
import { useEffect, useMemo, useRef, useState } from 'react';
import messagerieSocket from '../../socket/messagerieSocket';
import useGrandeEcole from '../../stores/useGrandeEcole';


export function MessagerieManager({ darkMode = true }) {

  const { isOpen, recipient, closeMessaging, submitMessage } = useGrandeEcole();
  
  const [view, setView] = useState('inbox'); // 'inbox' | 'sent' | 'starred' | 'archived' | 'compose'
  const [messages, setMessages] = useState([]);
  const [selectedMessage, setSelectedMessage] = useState(null);
  const [loading, setLoading] = useState(false);
  const [unreadCount, setUnreadCount] = useState(0);
  
  // Recherche & Filtres
  const [search, setSearch] = useState('');
  const [showAdvancedFilters, setShowAdvancedFilters] = useState(false);
  const [filters, setFilters] = useState({
    type: 'all',
    status: 'all',
    priority: 'all',
    dateRange: 'all',
    hasAttachments: false,
    labels: []
  });

  const [pagination, setPagination] = useState({ page: 1, limit: 20, total: 0, pages: 0 });

  // Compose enrichi
  const [composeData, setComposeData] = useState({
    to: [],
    cc: [],
    bcc: [],
    subject: '',
    body: '',
    priority: 'normal',
    attachments: [],
    template: null,
    scheduledAt: null,
    requireReadReceipt: false,
    encrypted: false
  });

  // Threads conversations
  const [threadView, setThreadView] = useState(false);
  const [currentThread, setCurrentThread] = useState(null);

  // Typing indicator
  const [typingUsers, setTypingUsers] = useState(new Set());

  // Notifications
  const [notificationsEnabled, setNotificationsEnabled] = useState(false);
  const [selectedMessages, setSelectedMessages] = useState(new Set());

  // Réponse rapide
  const [replyMode, setReplyMode] = useState(false);
  const [replyBody, setReplyBody] = useState('');

  // Brouillons auto-save
  const [draftTimer, setDraftTimer] = useState(null);

  // Templates
  const [templates, setTemplates] = useState([
    { id: 'welcome', name: 'Bienvenue', subject: 'Bienvenue sur la plateforme', body: 'Bonjour,\n\nNous sommes ravis de vous accueillir...' },
    { id: 'reminder', name: 'Rappel', subject: 'Rappel important', body: 'Bonjour,\n\nCeci est un rappel concernant...' },
    { id: 'followup', name: 'Suivi', subject: 'Suivi de notre échange', body: 'Bonjour,\n\nJe reviens vers vous concernant...' }
  ]);

  // Labels personnalisés
  const [labels, setLabels] = useState([
    { id: 'urgent', name: 'Urgent', color: 'red' },
    { id: 'important', name: 'Important', color: 'orange' },
    { id: 'later', name: 'À traiter plus tard', color: 'blue' },
    { id: 'done', name: 'Traité', color: 'green' }
  ]);

  const messagesEndRef = useRef(null);

  useEffect(() => {
  messagerieSocket.connect();

  const refreshData = () => {
    setLoading(true);
    if (view === 'sent') {
      console.log("📡 Demande de la liste 'Sent'...");
      messagerieSocket.getSent({ 
        page: pagination.page, 
        limit: pagination.limit 
      });
    } else if (view === 'inbox') {
      console.log("📡 Demande de la liste 'Inbox'...");
      loadInbox();
    }
    messagerieSocket.getUnreadCount();
  };

  refreshData();

  // 1. Gestion de la LISTE complète (retour du getSent)
  const handleSentList = (data) => {
    console.log("📥 Liste 'Sent' reçue du serveur:", data);
    if (view === 'sent') {
      setMessages(data.messages || []);
      setPagination(data.pagination || pagination);
      setLoading(false);
    }
  };

  // 2. Gestion de l'ajout en TEMPS RÉEL (quand on vient d'envoyer un message)
  const handleSingleSentUpdate = (data) => {
    console.log("📩 Nouveau message envoyé détecté (temps réel):", data);
    if (view === 'sent' && data.message) {
      setMessages(prev => {
        if (prev.find(m => m._id === data.message._id)) return prev;
        return [data.message, ...prev];
      });
    }
  };

  // BRANCHEMENT sur les noms émis par messagerieSocket.js
  // Note : on utilise 'sent' car le socket fait this.emit('sent', data)
  const unsubSentList = messagerieSocket.on('sent', handleSentList);
  
  // Note : on utilise 'message-sent' car ton socket fait this.emit('message-sent', data)
  const unsubSingleSent = messagerieSocket.on('message-sent', handleSingleSentUpdate);

  const unsubInbox = messagerieSocket.on('inbox', (data) => {
    if (view === 'inbox') {
      setMessages(data.messages || []);
      setPagination(data.pagination || pagination);
      setLoading(false);
    }
  });

  return () => {
    unsubSentList();
    unsubSingleSent();
    unsubInbox();
  };
}, [view, pagination.page]);

  // Auto-save brouillon
  useEffect(() => {
    if (view === 'compose' && (composeData.subject || composeData.body)) {
      if (draftTimer) clearTimeout(draftTimer);
      
      const timer = setTimeout(() => {
        saveDraft();
      }, 2000); // Sauvegarde après 2 secondes d'inactivité

      setDraftTimer(timer);
    }

    return () => {
      if (draftTimer) clearTimeout(draftTimer);
    };
  }, [composeData.subject, composeData.body]);

  useEffect(() => {
  // On écoute spécifiquement le succès de l'envoi
  const handleSentSuccess = (data) => {
    console.log("🚀 Message envoyé détecté, mise à jour de la liste...");
    
    // Si on est dans l'onglet "Sent", on ajoute le message manuellement
    if (view === 'sent') {
      // On s'assure que data.message contient bien le format du modèle
      setMessages(prev => [data.message, ...prev]);
    }
  };

  messagerieSocket.on('messagerie:get-sent', handleSentSuccess);
  return () => messagerieSocket.off('messagerie:get-sent', handleSentSuccess);
}, [view]);

  // Indicateur typing
  const handleTyping = () => {
    if (replyMode && selectedMessage) {
      messagerieSocket.emit('user-typing', {
        threadId: selectedMessage.threadId || selectedMessage._id,
        userId: 'currentUserId' // À remplacer par vrai userId
      });
    }
  };

  const loadInbox = () => {
    setLoading(true);
    messagerieSocket.getInbox({
      page: pagination.page,
      limit: pagination.limit,
      messageType: filters.type !== 'all' ? filters.type : undefined,
      status: filters.status !== 'all' ? filters.status : undefined,
      search: search || undefined
    });
  };

  const handleSelectMessage = (message) => {
    setSelectedMessage(message);
    
    // Marquer comme lu
    if (message.status !== 'read') {
      messagerieSocket.markAsRead(message._id);
    }

    // Charger thread si applicable
    if (message.threadId) {
      loadThread(message.threadId);
    }
  };

  // Charger thread complet
  const loadThread = async (threadId) => {
    try {
      const response = await fetch(`/api/admin/messagerie/thread/${threadId}`);
      const data = await response.json();
      setCurrentThread(data.messages || []);
      setThreadView(true);
    } catch (err) {
      console.error('Erreur chargement thread:', err);
    }
  };

  // Envoyer message enrichi
  const handleSendMessage = async () => {
    if (!composeData.to.length || !composeData.subject || !composeData.body) {
      showToast('❌ Destinataire, sujet et corps obligatoires', 'error');
      return;
    }

    try {
      // Upload attachments si nécessaire
      let attachmentUrls = [];
      if (composeData.attachments.length > 0) {
        attachmentUrls = await uploadAttachments(composeData.attachments);
      }

      messagerieSocket.sendMessage({
        to: composeData.to,
        cc: composeData.cc,
        bcc: composeData.bcc,
        subject: composeData.subject,
        body: composeData.body,
        messageType: 'conversation',
        metadata: {
          priority: composeData.priority,
          requireReadReceipt: composeData.requireReadReceipt,
          encrypted: composeData.encrypted,
          scheduledAt: composeData.scheduledAt
        },
        attachments: attachmentUrls
      });

      // Reset formulaire
      setComposeData({
        to: [],
        cc: [],
        bcc: [],
        subject: '',
        body: '',
        priority: 'normal',
        attachments: [],
        template: null,
        scheduledAt: null,
        requireReadReceipt: false,
        encrypted: false
      });

      // Supprimer brouillon
      clearDraft();

      setView('inbox');
      showToast('Message envoyé avec succès', 'success');
    } catch (err) {
      console.error('Erreur envoi message:', err);
      showToast(`❌ Erreur : ${err.message}`, 'error');
    }
  };

  // Upload pièces jointes
  const uploadAttachments = async (files) => {
    const formData = new FormData();
    files.forEach(file => formData.append('attachments', file));

    const response = await fetch('/api/admin/messagerie/upload', {
      method: 'POST',
      body: formData
    });

    if (!response.ok) throw new Error('Erreur upload');

    const data = await response.json();
    return data.urls;
  };

  // Ajouter pièce jointe
  const handleAddAttachment = (event) => {
    const files = Array.from(event.target.files);
    
    // Validation taille (max 10MB par fichier)
    const maxSize = 10 * 1024 * 1024;
    const validFiles = files.filter(f => {
      if (f.size > maxSize) {
        showToast(`❌ ${f.name} dépasse 10MB`, 'error');
        return false;
      }
      return true;
    });

    setComposeData(prev => ({
      ...prev,
      attachments: [...prev.attachments, ...validFiles]
    }));
  };

  // Retirer pièce jointe
  const handleRemoveAttachment = (index) => {
    setComposeData(prev => ({
      ...prev,
      attachments: prev.attachments.filter((_, i) => i !== index)
    }));
  };

  // Appliquer template
  const applyTemplate = (template) => {
    setComposeData(prev => ({
      ...prev,
      subject: template.subject,
      body: template.body,
      template: template.id
    }));
  };

  // Sauvegarder brouillon
  const saveDraft = () => {
    localStorage.setItem('messageDraft', JSON.stringify(composeData));
    console.log('💾 Brouillon sauvegardé');
  };

  // Charger brouillon
  const loadDraft = () => {
    const draft = localStorage.getItem('messageDraft');
    if (draft) {
      setComposeData(JSON.parse(draft));
      showToast('📄 Brouillon restauré', 'info');
    }
  };

  // Supprimer brouillon
  const clearDraft = () => {
    localStorage.removeItem('messageDraft');
  };

  // Marquer comme favori
  const toggleStar = async (messageId) => {
    try {
      await fetch(`/api/admin/messagerie/${messageId}/star`, {
        method: 'PUT'
      });
      
      setMessages(prev => prev.map(m => 
        m._id === messageId ? { ...m, isStarred: !m.isStarred } : m
      ));
    } catch (err) {
      console.error('Erreur toggle star:', err);
    }
  };

  // Ajouter label
  const addLabel = async (messageId, labelId) => {
    try {
      await fetch(`/api/admin/messagerie/${messageId}/label`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ labelId })
      });

      setMessages(prev => prev.map(m => {
        if (m._id === messageId) {
          const labels = m.labels || [];
          return { ...m, labels: [...labels, labelId] };
        }
        return m;
      }));
    } catch (err) {
      console.error('Erreur ajout label:', err);
    }
  };

  // Envoyer SMS (Twilio)
  const sendSMS = async (phoneNumber, message) => {
    try {
      const response = await fetch('/api/admin/messagerie/sms', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ to: phoneNumber, body: message })
      });

      if (!response.ok) throw new Error('Erreur envoi SMS');

      showToast('SMS envoyé avec succès', 'success');
    } catch (err) {
      console.error('Erreur SMS:', err);
      showToast(`❌ Erreur SMS : ${err.message}`, 'error');
    }
  };

  // Son notification
  const playNotificationSound = () => {
    const audio = new Audio('/sounds/notification.mp3');
    audio.volume = 0.3;
    audio.play().catch(err => console.log('Son bloqué par navigateur'));
  };

  const showToast = (message, type = 'info') => {
    console.log(`[TOAST ${type}]`, message);
    // TODO : Intégrer react-toastify
  };

  // Filtres avancés
  const filteredMessages = useMemo(() => {
    let result = [...messages];

    // Recherche full-text
    if (search) {
      const searchLower = search.toLowerCase();
      result = result.filter(m =>
        m.subject?.toLowerCase().includes(searchLower) ||
        m.body?.toLowerCase().includes(searchLower) ||
        m.from.name?.toLowerCase().includes(searchLower)
      );
    }

    // Filtre type
    if (filters.type !== 'all') {
      result = result.filter(m => m.messageType === filters.type);
    }

    // Filtre status
    if (filters.status !== 'all') {
      result = result.filter(m => m.status === filters.status);
    }

    // Filtre priorité
    if (filters.priority !== 'all') {
      result = result.filter(m => m.metadata?.priority === filters.priority);
    }

    // Filtre pièces jointes
    if (filters.hasAttachments) {
      result = result.filter(m => m.attachments && m.attachments.length > 0);
    }

    // Filtre labels
    if (filters.labels.length > 0) {
      result = result.filter(m => 
        m.labels && filters.labels.some(l => m.labels.includes(l))
      );
    }

    return result;
  }, [messages, search, filters]);

  const formatDate = (dateStr) => {
    if (!dateStr) return '—';
    try {
      const date = new Date(dateStr);
      const now = new Date();
      const diffDays = Math.floor((now - date) / (1000 * 60 * 60 * 24));

      if (diffDays === 0) {
        return format(date, 'HH:mm', { locale: fr });
      } else if (diffDays === 1) {
        return 'Hier';
      } else if (diffDays < 7) {
        return format(date, 'EEEE', { locale: fr });
      } else {
        return format(date, 'dd MMM yyyy', { locale: fr });
      }
    } catch {
      return dateStr;
    }
  };

  const getPriorityIcon = (priority) => {
    switch (priority) {
      case 'urgent': return <AlertCircle size={12} className="text-red-500" />;
      case 'high': return <AlertCircle size={12} className="text-orange-500" />;
      default: return null;
    }
  };

  const getMessageTypeIcon = (type) => {
    switch (type) {
      case 'credentials': return <Key size={14} className="text-orange-500" />;
      case 'notification': return <Bell size={14} className="text-sky-500" />;
      case 'alert': return <Shield size={14} className="text-red-500" />;
      default: return <Mail size={14} className="text-zinc-500" />;
    }
  };

  const getReadStatus = (message) => {
    if (message.readAt) {
      return <CheckCheck size={14} className="text-emerald-500" title="Lu" />;
    }
    if (message.status === 'delivered') {
      return <Check size={14} className="text-zinc-500" title="Livré" />;
    }
    return <Clock size={14} className="text-zinc-600" title="Envoyé" />;
  };

  const selectAllMessages = () => {
    const allIds = filteredMessages.map(l => l.userId || l._id || l.id);
    setSelectedMessages(new Set(allIds));
  };

  const clearSelection = () => {
    setSelectedMessages(new Set());
  };

  return (
    <div className="space-y-6 relative">
      {/* HEADER */}
      <div className="flex flex-col gap-4 border-b border-zinc-800 pb-4">

        {/* Ligne 1 : titre + cloche + compteurs */}
        <div className="flex items-start justify-between gap-3">
          <div>
            <h3 className="text-xl md:text-2xl font-black tracking-tighter text-sky-400 uppercase flex items-center gap-3">
              Messagerie
              {notificationsEnabled && (
                <Bell size={16} className="text-emerald-400 animate-pulse" title="Notifications activées" />
              )}
            </h3>
            <p className="text-[11px] uppercase tracking-widest text-zinc-500 mt-1">
              {unreadCount} message{unreadCount > 1 ? 's' : ''} non lu{unreadCount > 1 ? 's' : ''} · {messages.length} total
            </p>
          </div>

          {/* Actions droite : Nouveau + cloche */}
          <div className="flex items-center gap-2 shrink-0">
            <button
              onClick={() => { setView('compose'); loadDraft(); }}
              className="px-3 md:px-5 py-2 bg-sky-600/20 hover:bg-sky-600/40 border border-sky-500/30 text-sky-300 font-bold text-xs uppercase tracking-widest transition-all flex items-center gap-2 rounded"
            >
              <Send size={14} />
              <span className="hidden sm:inline">Nouveau</span>
            </button>
            <button
              onClick={() => {
                if (notificationsEnabled) {
                  setNotificationsEnabled(false);
                } else {
                  Notification.requestPermission().then(permission => {
                    setNotificationsEnabled(permission === 'granted');
                  });
                }
              }}
              className={`p-2 rounded transition-all ${
                notificationsEnabled
                  ? 'bg-emerald-600/20 text-emerald-400 border border-emerald-500/30'
                  : 'bg-zinc-900 text-zinc-500 border border-zinc-700'
              }`}
              title={notificationsEnabled ? 'Désactiver notifications' : 'Activer notifications'}
            >
              {notificationsEnabled ? <Bell size={14} /> : <BellOff size={14} />}
            </button>
          </div>
        </div>

        {/* Ligne 2 : tabs — scroll horizontal sur mobile */}
        <div className="flex items-center bg-zinc-900 border border-zinc-800 p-1 rounded overflow-x-auto scrollbar-none">
          {[
            { id: 'inbox',    label: 'Reçus',    icon: Mail,    badge: unreadCount },
            { id: 'sent',     label: 'Envoyés',  icon: Send,    badge: null },
            { id: 'starred',  label: 'Favoris',  icon: Star,    badge: null },
            { id: 'archived', label: 'Archives', icon: Archive, badge: null },
          ].map(tab => (
            <button
              key={tab.id}
              onClick={() => setView(tab.id)}
              className={`flex items-center gap-1.5 px-3 md:px-4 py-2 text-xs font-bold uppercase tracking-wider transition-all rounded shrink-0 ${
                view === tab.id
                  ? 'bg-sky-600 text-white'
                  : 'text-zinc-400 hover:text-zinc-200'
              }`}
            >
              <tab.icon size={13} />
              <span>{tab.label}</span>
              {tab.badge > 0 && (
                <span className={`text-[9px] font-black px-1.5 py-0.5 rounded-full ${
                  view === tab.id ? 'bg-white/20 text-white' : 'bg-sky-900/60 text-sky-300'
                }`}>
                  {tab.badge}
                </span>
              )}
            </button>
          ))}
        </div>

      </div>

      {/* BARRE RECHERCHE + FILTRES */}
      {view !== 'compose' && (
        <div className="flex items-center gap-2">
          {/* Recherche */}
          <div className="relative flex-1">
            <Search size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-zinc-500" />
            <input
              type="text"
              value={search}
              onChange={e => setSearch(e.target.value)}
              placeholder="Rechercher messages..."
              className="w-full pl-10 pr-4 py-2 text-sm font-mono bg-zinc-900/50 border border-zinc-800 text-zinc-200 placeholder-zinc-600 outline-none focus:border-sky-500/50 rounded"
            />
          </div>

          {/* Filtres */}
          <button
            onClick={() => setShowAdvancedFilters(!showAdvancedFilters)}
            className={`shrink-0 px-3 md:px-4 py-2 text-xs font-bold uppercase border rounded transition-all flex items-center gap-1.5 ${
              showAdvancedFilters
                ? 'bg-sky-600/30 border-sky-500/50 text-sky-300'
                : 'bg-zinc-900 border-zinc-700 text-zinc-400 hover:text-zinc-200'
            }`}
          >
            <Filter size={14} />
            <span className="hidden sm:inline">Filtres</span>
          </button>

          {/* Actualiser */}
          <button
            onClick={loadInbox}
            className="shrink-0 px-3 md:px-4 py-2 bg-zinc-900 border border-zinc-700 text-zinc-400 hover:text-zinc-200 text-xs font-bold uppercase rounded flex items-center gap-1.5"
          >
            <RotateCw size={14} />
            <span className="hidden sm:inline">Actualiser</span>
          </button>
        </div>
      )}

      {/* PANNEAU FILTRES AVANCÉS */}
      {showAdvancedFilters && view !== 'compose' && (
        <div className="bg-zinc-900/50 border border-zinc-800 p-4 rounded space-y-4 animate-in fade-in slide-in-from-top-2">
          <h4 className="text-sm font-bold text-zinc-300 uppercase tracking-wide">Filtres Avancés</h4>

          <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-5 gap-4">
            {/* Type */}
            <div>
              <label className="block text-[10px] uppercase text-zinc-500 mb-2">Type</label>
              <select value={filters.type} onChange={e => setFilters({ ...filters, type: e.target.value })}
                className="w-full bg-zinc-900 border border-zinc-700 px-3 py-2 text-sm outline-none focus:border-sky-500 rounded">
                <option value="all">Tous</option>
                <option value="conversation">Conversations</option>
                <option value="credentials">Identifiants</option>
                <option value="notification">Notifications</option>
                <option value="alert">Alertes</option>
              </select>
            </div>

            {/* Priorité */}
            <div>
              <label className="block text-[10px] uppercase text-zinc-500 mb-2">Priorité</label>
              <select value={filters.priority} onChange={e => setFilters({ ...filters, priority: e.target.value })}
                className="w-full bg-zinc-900 border border-zinc-700 px-3 py-2 text-sm outline-none focus:border-sky-500 rounded">
                <option value="all">Toutes</option>
                <option value="urgent">Urgent</option>
                <option value="high">Haute</option>
                <option value="normal">Normale</option>
                <option value="low">Basse</option>
              </select>
            </div>

            {/* Période */}
            <div>
              <label className="block text-[10px] uppercase text-zinc-500 mb-2">Période</label>
              <select value={filters.dateRange} onChange={e => setFilters({ ...filters, dateRange: e.target.value })}
                className="w-full bg-zinc-900 border border-zinc-700 px-3 py-2 text-sm outline-none focus:border-sky-500 rounded">
                <option value="all">Toutes dates</option>
                <option value="today">Aujourd'hui</option>
                <option value="week">Cette semaine</option>
                <option value="month">Ce mois</option>
              </select>
            </div>

            {/* Pièces jointes */}
            <div className="flex items-end pb-1">
              <label className="flex items-center gap-2 cursor-pointer">
                <input type="checkbox" checked={filters.hasAttachments}
                  onChange={e => setFilters({ ...filters, hasAttachments: e.target.checked })}
                  className="w-4 h-4 bg-zinc-800 border-zinc-600 rounded focus:ring-sky-500" />
                <span className="text-sm text-zinc-300">Pièces jointes</span>
              </label>
            </div>

            {/* Labels */}
            <div className="sm:col-span-2 md:col-span-1">
              <label className="block text-[10px] uppercase text-zinc-500 mb-2">Labels</label>
              <div className="flex flex-wrap gap-2">
                {labels.map(label => (
                  <button
                    key={label.id}
                    onClick={() => {
                      const newLabels = filters.labels.includes(label.id)
                        ? filters.labels.filter(l => l !== label.id)
                        : [...filters.labels, label.id];
                      setFilters({ ...filters, labels: newLabels });
                    }}
                    className={`px-2 py-1 text-xs rounded border transition-all ${
                      filters.labels.includes(label.id)
                        ? `bg-${label.color}-950/30 border-${label.color}-800/50 text-${label.color}-300`
                        : 'bg-zinc-900 border-zinc-700 text-zinc-500 hover:text-zinc-300'
                    }`}
                  >
                    {label.name}
                  </button>
                ))}
              </div>
            </div>
          </div>

          <div className="flex justify-end gap-2">
            <button
              onClick={() => setFilters({ type: 'all', status: 'all', priority: 'all', dateRange: 'all', hasAttachments: false, labels: [] })}
              className="px-4 py-2 bg-zinc-800 hover:bg-zinc-700 border border-zinc-600 text-zinc-300 text-xs rounded">
              Réinitialiser
            </button>
            <button onClick={() => setShowAdvancedFilters(false)}
              className="px-4 py-2 bg-sky-600 hover:bg-sky-700 text-white text-xs font-medium rounded">
              Appliquer
            </button>
          </div>
        </div>
      )}

      {/* CONTENU PRINCIPAL */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">

        {/* LISTE MESSAGES */}
        {view !== 'compose' && (
          <div className={`${selectedMessage ? 'lg:col-span-1' : 'lg:col-span-3'} border border-zinc-800 bg-black/40 overflow-hidden rounded-lg`}>
            {/* Header liste — desktop uniquement */}
            <div className="hidden md:grid grid-cols-12 gap-4 px-4 py-3 text-[10px] font-black uppercase tracking-widest border-b bg-zinc-900/60 border-zinc-700 text-zinc-400">
              <div className="col-span-1 flex items-center justify-center">
                <input
                  type="checkbox"
                  checked={selectedMessages.size === filteredMessages.length && filteredMessages.length > 0}  // Adapte avec ton state
                  onChange={(e) => {
                    if (e.target.checked) {
                      selectAllMessages();  // Ta fonction select all
                    } else {
                      clearSelection();     // Ta fonction clear
                    }
                  }}
                  className="w-4 h-4 bg-zinc-800 border-zinc-600 rounded focus:ring-orange-500 accent-orange-500 cursor-pointer"
                />
              </div>
              <div className="col-span-1 text-center">Type</div>
              <div className="col-span-3">De</div>
              <div className="col-span-5">Sujet</div>
              <div className="col-span-2 text-right">Date</div>
            </div>

            {/* Messages */}
            <div className="divide-y divide-zinc-800/40 max-h-[600px] overflow-y-auto">
              {loading ? (
                <div className="py-20 text-center text-zinc-500">
                  <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-sky-500 mx-auto mb-4" />
                  Chargement...
                </div>
              ) : filteredMessages.length > 0 ? (
                filteredMessages.map(msg => (
                  <div
                    key={msg._id}
                    onClick={() => handleSelectMessage(msg)}
                    className={`cursor-pointer transition-colors ${
                      msg.status !== 'read' ? 'bg-sky-950/10' : ''
                    } ${
                      selectedMessage?._id === msg._id
                        ? 'bg-sky-900/20 border-l-2 border-sky-500'
                        : 'hover:bg-sky-950/5'
                    }`}
                  >

                    {/* ═══════════════════════════════════ */}
                    {/* VUE MOBILE : carte compacte         */}
                    {/* ═══════════════════════════════════ */}
                    <div className="md:hidden px-4 py-3 space-y-1.5">
                      {/* Ligne 1 : type + expéditeur + date + star */}
                      <div className="flex items-center gap-2">
                        <button
                          onClick={e => { e.stopPropagation(); toggleStar(msg._id); }}
                          className="shrink-0"
                        >
                          {msg.isStarred
                            ? <Star size={13} className="text-amber-400 fill-amber-400" />
                            : <Star size={13} className="text-zinc-700 hover:text-amber-400" />
                          }
                        </button>
                        <div className="shrink-0">{getMessageTypeIcon(msg.messageType)}</div>
                        <span className={`flex-1 text-sm truncate ${msg.status !== 'read' ? 'font-bold text-zinc-200' : 'text-zinc-400'}`}>
                          {msg.from.type === 'system' ? '⚙️ Système' : msg.from.name}
                        </span>
                        <span className="shrink-0 text-[10px] text-zinc-600 font-mono">
                          {formatDate(msg.sentAt)}
                        </span>
                        <div className="shrink-0">{getReadStatus(msg)}</div>
                      </div>
                      {/* Ligne 2 : sujet + extrait */}
                      <div className="pl-[42px]">
                        <div className={`text-sm truncate ${msg.status !== 'read' ? 'font-semibold text-zinc-200' : 'text-zinc-400'}`}>
                          {msg.attachments?.length > 0 && <Paperclip size={11} className="inline mr-1 text-zinc-500" />}
                          {msg.subject}
                        </div>
                        <div className="text-[11px] text-zinc-600 truncate">{msg.body.substring(0, 60)}...</div>
                        {msg.labels?.length > 0 && (
                          <div className="flex gap-1 mt-1 flex-wrap">
                            {msg.labels.map(labelId => {
                              const label = labels.find(l => l.id === labelId);
                              return label ? (
                                <span key={labelId}
                                  className={`text-[9px] px-1.5 py-0.5 rounded-full bg-${label.color}-950/30 text-${label.color}-400 border border-${label.color}-800/30`}>
                                  {label.name}
                                </span>
                              ) : null;
                            })}
                          </div>
                        )}
                      </div>
                    </div>

                    {/* ═══════════════════════════════════ */}
                    {/* VUE DESKTOP md+ : tableau 12 col    */}
                    {/* ═══════════════════════════════════ */}
                    <div className="hidden md:grid grid-cols-12 gap-4 px-4 py-3 items-center">
                      {/* Star */}
                      <div className="col-span-1 flex justify-center">
                        <button onClick={e => { e.stopPropagation(); toggleStar(msg._id); }}
                          className="opacity-0 group-hover:opacity-100 transition-opacity">
                          {msg.isStarred
                            ? <Star size={14} className="text-amber-400 fill-amber-400" />
                            : <Star size={14} className="text-zinc-600 hover:text-amber-400" />
                          }
                        </button>
                      </div>
                      {/* Type */}
                      <div className="col-span-1 flex justify-center">
                        {getMessageTypeIcon(msg.messageType)}
                      </div>
                      {/* De */}
                      <div className="col-span-3 truncate">
                        <div className="flex items-center gap-2">
                          {getPriorityIcon(msg.metadata?.priority)}
                          <span className={`text-sm truncate ${msg.status !== 'read' ? 'font-bold text-zinc-200' : 'text-zinc-400'}`}>
                            {msg.from.type === 'system' ? '⚙️ Système' : msg.from.name}
                          </span>
                        </div>
                      </div>
                      {/* Sujet */}
                      <div className="col-span-5 truncate">
                        <div className="flex items-center gap-2">
                          {msg.attachments?.length > 0 && <Paperclip size={12} className="text-zinc-500" />}
                          <span className={`text-sm truncate ${msg.status !== 'read' ? 'font-bold text-zinc-200' : 'text-zinc-400'}`}>
                            {msg.subject}
                          </span>
                        </div>
                        <div className="text-xs text-zinc-600 truncate mt-0.5">{msg.body.substring(0, 50)}...</div>
                        {msg.labels?.length > 0 && (
                          <div className="flex gap-1 mt-1">
                            {msg.labels.map(labelId => {
                              const label = labels.find(l => l.id === labelId);
                              return label ? (
                                <span key={labelId}
                                  className={`text-[9px] px-2 py-0.5 rounded-full bg-${label.color}-950/30 text-${label.color}-400 border border-${label.color}-800/30`}>
                                  {label.name}
                                </span>
                              ) : null;
                            })}
                          </div>
                        )}
                      </div>
                      {/* Date + statut */}
                      <div className="col-span-2 text-right">
                        <div className="text-xs text-zinc-500 font-mono">{formatDate(msg.sentAt)}</div>
                        <div className="flex justify-end mt-1">{getReadStatus(msg)}</div>
                      </div>
                    </div>

                  </div>
                ))
              ) : (
                <div className="py-20 text-center text-zinc-500">
                  <Mail size={48} className="mx-auto mb-4 opacity-40" />
                  Aucun message
                </div>
              )}
            </div>

            {/* Pagination */}
            {pagination.pages > 1 && (
              <div className="flex items-center justify-between px-4 py-3 border-t border-zinc-800 bg-zinc-900/40">
                <div className="text-xs text-zinc-500">
                  Page {pagination.page} / {pagination.pages} ({pagination.total})
                </div>
                <div className="flex gap-2">
                  <button onClick={() => setPagination(prev => ({ ...prev, page: prev.page - 1 }))}
                    disabled={pagination.page === 1}
                    className="p-1.5 border border-zinc-700 hover:bg-zinc-800 disabled:opacity-40 disabled:cursor-not-allowed rounded">
                    <ChevronLeft size={14} />
                  </button>
                  <button onClick={() => setPagination(prev => ({ ...prev, page: prev.page + 1 }))}
                    disabled={pagination.page === pagination.pages}
                    className="p-1.5 border border-zinc-700 hover:bg-zinc-800 disabled:opacity-40 disabled:cursor-not-allowed rounded">
                    <ChevronRight size={14} />
                  </button>
                </div>
              </div>
            )}
          </div>
        )}
        {/* DÉTAIL MESSAGE / VUE THREAD */}
        {selectedMessage && view !== 'compose' && (
          <div className="lg:col-span-2 border border-zinc-800 bg-black/40 rounded-lg overflow-hidden flex flex-col max-h-[700px]">
            {/* Header message */}
            <div className="flex items-center justify-between px-4 py-3 border-b border-zinc-800 bg-zinc-900/60 flex-shrink-0">
              <div className="flex items-center gap-3 flex-1 min-w-0">
                {getMessageTypeIcon(selectedMessage.messageType)}
                <div className="flex-1 min-w-0">
                  <div className="text-sm font-bold text-zinc-200 truncate">
                    {selectedMessage.subject}
                  </div>
                  <div className="text-xs text-zinc-500">
                    De : {selectedMessage.from.type === 'system' ? 'Système' : selectedMessage.from.name}
                  </div>
                </div>
              </div>

              <div className="flex items-center gap-1 flex-shrink-0 overflow-x-auto scrollbar-none max-w-[50vw] md:max-w-none">
                {/* Toggle thread */}
                {selectedMessage.threadId && (
                  <button
                    onClick={() => setThreadView(!threadView)}
                    className={`p-2 rounded transition-colors ${
                      threadView
                        ? 'bg-purple-600/20 text-purple-400 border border-purple-500/30'
                        : 'hover:bg-zinc-800 border border-zinc-700'
                    }`}
                    title={threadView ? 'Masquer thread' : 'Voir thread complet'}
                  >
                    <MessageCircle size={14} />
                  </button>
                )}

                {/* Star */}
                <button
                  onClick={() => toggleStar(selectedMessage._id)}
                  className="p-2 hover:bg-zinc-800 border border-zinc-700 rounded transition-colors"
                  title={selectedMessage.isStarred ? 'Retirer des favoris' : 'Ajouter aux favoris'}
                >
                  {selectedMessage.isStarred ? (
                    <Star size={14} className="text-amber-400 fill-amber-400" />
                  ) : (
                    <Star size={14} className="text-zinc-500" />
                  )}
                </button>

                {/* Labels menu */}
                <div className="relative group">
                  <button className="p-2 hover:bg-zinc-800 border border-zinc-700 rounded transition-colors">
                    <Tag size={14} />
                  </button>
                  <div className="absolute right-0 mt-2 w-48 bg-zinc-900 border border-zinc-700 rounded shadow-lg opacity-0 invisible group-hover:opacity-100 group-hover:visible transition-all z-50">
                    {labels.map(label => (
                      <button
                        key={label.id}
                        onClick={() => addLabel(selectedMessage._id, label.id)}
                        className={`w-full px-4 py-2 text-left text-xs hover:bg-zinc-800 transition-colors flex items-center gap-2 text-${label.color}-400`}
                      >
                        <div className={`w-2 h-2 rounded-full bg-${label.color}-500`} />
                        {label.name}
                      </button>
                    ))}
                  </div>
                </div>

                {/* Actions menu */}
                <div className="relative group">
                  <button className="p-2 hover:bg-zinc-800 border border-zinc-700 rounded transition-colors">
                    <MoreVertical size={14} />
                  </button>
                  <div className="absolute right-0 mt-2 w-48 bg-zinc-900 border border-zinc-700 rounded shadow-lg opacity-0 invisible group-hover:opacity-100 group-hover:visible transition-all z-50">
                    <button
                      onClick={() => {
                        messagerieSocket.archiveMessage(selectedMessage._id);
                        setSelectedMessage(null);
                      }}
                      className="w-full px-4 py-2 text-left text-xs hover:bg-zinc-800 text-zinc-300 flex items-center gap-2"
                    >
                      <Archive size={12} />
                      Archiver
                    </button>
                    <button
                      onClick={() => {
                        if (window.confirm('Supprimer ce message ?')) {
                          messagerieSocket.deleteMessage(selectedMessage._id);
                          setSelectedMessage(null);
                        }
                      }}
                      className="w-full px-4 py-2 text-left text-xs hover:bg-zinc-800 text-red-400 flex items-center gap-2"
                    >
                      <Trash2 size={12} />
                      Supprimer
                    </button>
                    {selectedMessage.from.type !== 'system' && (
                      <button
                        onClick={() => {
                          // TODO: Implémenter envoi SMS
                          const phone = prompt('Numéro de téléphone :');
                          if (phone) {
                            sendSMS(phone, `Re: ${selectedMessage.subject}\n\n${selectedMessage.body.substring(0, 100)}...`);
                          }
                        }}
                        className="w-full px-4 py-2 text-left text-xs hover:bg-zinc-800 text-emerald-400 flex items-center gap-2"
                      >
                        <Smartphone size={12} />
                        Envoyer par SMS
                      </button>
                    )}
                  </div>
                </div>

                {/* Répondre */}
                {!replyMode && selectedMessage.from.userId !== 'system' && (
                  <button
                    onClick={() => setReplyMode(true)}
                    className="p-2 hover:bg-zinc-800 border border-zinc-700 rounded transition-colors"
                    title="Répondre"
                  >
                    <Reply size={14} />
                  </button>
                )}

                {/* Fermer */}
                <button
                  onClick={() => {
                    setSelectedMessage(null);
                    setThreadView(false);
                    setReplyMode(false);
                  }}
                  className="p-2 hover:bg-zinc-800 border border-zinc-700 rounded transition-colors"
                  title="Fermer"
                >
                  <X size={14} />
                </button>
              </div>
            </div>

            {/* Corps message / Thread */}
            <div className="flex-1 overflow-y-auto">
              {threadView && currentThread ? (
                // VUE THREAD COMPLÈTE
                <div className="p-6 space-y-4">
                  <div className="flex items-center gap-2 mb-4 pb-3 border-b border-zinc-800">
                    <MessageCircle size={16} className="text-purple-400" />
                    <span className="text-sm font-bold text-purple-300 uppercase tracking-wide">
                      Conversation ({currentThread.length} messages)
                    </span>
                  </div>

                  {currentThread.map((msg, idx) => (
                    <div
                      key={msg._id}
                      className={`p-4 rounded border ${
                        msg._id === selectedMessage._id
                          ? 'bg-sky-950/20 border-sky-800/50'
                          : 'bg-zinc-950/50 border-zinc-800/30'
                      }`}
                    >
                      {/* Header message thread */}
                      <div className="flex items-start justify-between mb-3 pb-2 border-b border-zinc-800/30">
                        <div className="flex items-center gap-3">
                          <div className="w-8 h-8 rounded-full bg-gradient-to-br from-sky-500/20 to-purple-500/20 flex items-center justify-center text-sky-400 font-bold text-xs border border-sky-500/30">
                            {msg.from.name?.slice(0, 2).toUpperCase() || '??'}
                          </div>
                          <div>
                            <div className="text-sm font-medium text-zinc-200">
                              {msg.from.type === 'system' ? 'Système' : msg.from.name}
                            </div>
                            <div className="text-xs text-zinc-500">
                              {format(new Date(msg.sentAt), 'dd MMM yyyy à HH:mm', { locale: fr })}
                            </div>
                          </div>
                        </div>
                        {getReadStatus(msg)}
                      </div>

                      {/* Corps message thread */}
                      <div className="text-sm text-zinc-300 leading-relaxed whitespace-pre-wrap">
                        {msg.body}
                      </div>

                      {/* Pièces jointes thread */}
                      {msg.attachments && msg.attachments.length > 0 && (
                        <div className="mt-3 pt-3 border-t border-zinc-800/30">
                          <div className="text-xs text-zinc-500 mb-2 flex items-center gap-1">
                            <Paperclip size={12} />
                            Pièces jointes ({msg.attachments.length})
                          </div>
                          <div className="flex flex-wrap gap-2">
                            {msg.attachments.map((att, i) => (
                              <a
                                key={i}
                                href={att.url}
                                target="_blank"
                                rel="noopener noreferrer"
                                className="flex items-center gap-2 px-3 py-2 bg-zinc-900/50 border border-zinc-700 rounded text-xs text-zinc-300 hover:bg-zinc-800 transition-colors"
                              >
                                <Paperclip size={12} />
                                <span className="truncate max-w-[150px]">{att.name}</span>
                                <Download size={12} />
                              </a>
                            ))}
                          </div>
                        </div>
                      )}
                    </div>
                  ))}

                  {/* Typing indicator dans thread */}
                  {typingUsers.size > 0 && (
                    <div className="flex items-center gap-2 text-xs text-zinc-500 italic">
                      <div className="flex gap-1">
                        <div className="w-2 h-2 bg-sky-500 rounded-full animate-bounce" style={{ animationDelay: '0ms' }} />
                        <div className="w-2 h-2 bg-sky-500 rounded-full animate-bounce" style={{ animationDelay: '150ms' }} />
                        <div className="w-2 h-2 bg-sky-500 rounded-full animate-bounce" style={{ animationDelay: '300ms' }} />
                      </div>
                      En train d'écrire...
                    </div>
                  )}
                </div>
              ) : (
                // VUE MESSAGE UNIQUE
                <div className="p-6 space-y-4">
                  {/* Métadonnées */}
                  <div className="flex flex-wrap items-center gap-3 text-xs text-zinc-500 pb-4 border-b border-zinc-800">
                    <div className="flex items-center gap-2">
                      <Clock size={12} />
                      {format(new Date(selectedMessage.sentAt), 'dd MMMM yyyy à HH:mm', { locale: fr })}
                    </div>
                    
                    {selectedMessage.metadata?.priority && selectedMessage.metadata.priority !== 'normal' && (
                      <div className={`px-2 py-0.5 border text-[10px] font-bold uppercase rounded ${
                        selectedMessage.metadata.priority === 'urgent'
                          ? 'border-red-500/30 text-red-500'
                          : selectedMessage.metadata.priority === 'high'
                          ? 'border-orange-500/30 text-orange-500'
                          : 'border-zinc-700 text-zinc-500'
                      }`}>
                        Priorité {selectedMessage.metadata.priority}
                      </div>
                    )}

                    {selectedMessage.metadata?.encrypted && (
                      <div className="flex items-center gap-1 text-emerald-400">
                        <Lock size={12} />
                        Chiffré E2E
                      </div>
                    )}

                    {selectedMessage.metadata?.requireReadReceipt && (
                      <div className="flex items-center gap-1 text-sky-400">
                        <Eye size={12} />
                        Accusé de lecture
                      </div>
                    )}
                  </div>

                  {/* Destinataires (si multiple) */}
                  {selectedMessage.to && selectedMessage.to.length > 1 && (
                    <div className="text-xs text-zinc-500">
                      <span className="font-medium">À :</span>{' '}
                      {selectedMessage.to.map(t => t.name).join(', ')}
                    </div>
                  )}

                  {/* Contenu */}
                  <div className="prose prose-invert prose-sm max-w-none">
                    <div className="whitespace-pre-wrap text-zinc-300 leading-relaxed">
                      {selectedMessage.body}
                    </div>
                  </div>

                  {/* Métadonnées spéciales (credentials) */}
                  {selectedMessage.messageType === 'credentials' && selectedMessage.metadata?.tempPassword && (
                    <div className="mt-6 p-4 bg-orange-950/20 border border-orange-800/50 rounded-lg">
                      <div className="flex items-center gap-2 mb-3">
                        <Key size={16} className="text-orange-500" />
                        <div className="text-sm font-bold text-orange-300">Identifiants Apprenant</div>
                      </div>
                      <div className="space-y-2 text-sm font-mono">
                        <div>
                          <span className="text-zinc-500">Email :</span>{' '}
                          <span className="text-zinc-200">{selectedMessage.metadata.email || '—'}</span>
                        </div>
                        <div>
                          <span className="text-zinc-500">Mot de passe temporaire :</span>{' '}
                          <span className="text-orange-400 font-bold">{selectedMessage.metadata.tempPassword}</span>
                        </div>
                      </div>
                      <div className="mt-3 text-xs text-zinc-500 italic flex items-center gap-1">
                        <Shield size={12} />
                        Transmettez ces identifiants de manière sécurisée.
                      </div>
                    </div>
                  )}

                  {/* Pièces jointes */}
                  {selectedMessage.attachments && selectedMessage.attachments.length > 0 && (
                    <div className="mt-6 p-4 bg-zinc-950/50 border border-zinc-800 rounded-lg">
                      <div className="flex items-center gap-2 mb-3">
                        <Paperclip size={16} className="text-sky-400" />
                        <div className="text-sm font-bold text-sky-300">
                          Pièces jointes ({selectedMessage.attachments.length})
                        </div>
                      </div>
                      <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
                        {selectedMessage.attachments.map((att, idx) => (
                          <a
                            key={idx}
                            href={att.url}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="flex items-center gap-3 p-3 bg-zinc-900/50 border border-zinc-700 rounded hover:bg-zinc-800 transition-colors group"
                          >
                            <Paperclip size={14} className="text-zinc-500 group-hover:text-sky-400" />
                            <div className="flex-1 min-w-0">
                              <div className="text-sm text-zinc-300 truncate">{att.name}</div>
                              <div className="text-xs text-zinc-600">
                                {att.type} · {(att.size / 1024).toFixed(1)} KB
                              </div>
                            </div>
                            <Download size={14} className="text-zinc-500 group-hover:text-sky-400" />
                          </a>
                        ))}
                      </div>
                    </div>
                  )}
                </div>
              )}
            </div>

            {/* FORMULAIRE RÉPONSE */}
            {replyMode && (
              <div className="p-4 border-t border-zinc-800 bg-zinc-900/40 flex-shrink-0">
                <div className="mb-2 text-xs text-zinc-500">
                  Répondre à : {selectedMessage.from.name}
                </div>
                <textarea
                  value={replyBody}
                  onChange={(e) => {
                    setReplyBody(e.target.value);
                    handleTyping();
                  }}
                  placeholder="Votre réponse..."
                  rows={4}
                  className="w-full bg-zinc-900 border border-zinc-700 rounded px-3 py-2 text-sm outline-none focus:border-sky-500 resize-none"
                />
                <div className="flex justify-end gap-2 mt-3">
                  <button
                    onClick={() => { setReplyMode(false); setReplyBody(''); }}
                    className="px-4 py-2 bg-zinc-800 hover:bg-zinc-700 border border-zinc-600 text-zinc-300 text-sm rounded"
                  >
                    Annuler
                  </button>
                  <button
                    onClick={() => {
                      messagerieSocket.replyToMessage(selectedMessage._id, replyBody);
                      setReplyMode(false);
                      setReplyBody('');
                      showToast('Réponse envoyée', 'success');
                    }}
                    className="px-5 py-2 bg-sky-600 hover:bg-sky-700 text-white text-sm font-medium rounded flex items-center gap-2"
                  >
                    <Send size={14} />
                    Envoyer
                  </button>
                </div>
              </div>
            )}
          </div>
        )}
        {/* COMPOSER MESSAGE ENRICHI */}
        {view === 'compose' && (
          <div className="lg:col-span-3 border border-zinc-800 bg-black/40 rounded-lg p-4 md:p-6">
            <div className="flex items-center justify-between mb-6 gap-3">
              <h4 className="text-lg font-black text-sky-300">Nouveau message</h4>
              <div className="flex items-center gap-2">
                {localStorage.getItem('messageDraft') && (
                  <span className="text-xs text-emerald-400 hidden sm:flex items-center gap-1">
                    <Bookmark size={12} />
                    Brouillon sauvegardé
                  </span>
                )}

                {/* Templates */}
                <div className="relative group">
                  <button className="px-3 py-1.5 bg-zinc-900 border border-zinc-700 text-zinc-400 hover:text-zinc-200 text-xs rounded flex items-center gap-2">
                    <FileText size={12} />
                    Templates
                  </button>
                  <div className="absolute right-0 mt-2 w-64 bg-zinc-900 border border-zinc-700 rounded shadow-lg opacity-0 invisible group-hover:opacity-100 group-hover:visible transition-all z-50">
                    {templates.map(template => (
                      <button
                        key={template.id}
                        onClick={() => applyTemplate(template)}
                        className="w-full px-4 py-3 text-left hover:bg-zinc-800 transition-colors border-b border-zinc-800 last:border-b-0"
                      >
                        <div className="text-sm text-zinc-200 font-medium">{template.name}</div>
                        <div className="text-xs text-zinc-500 mt-1 truncate">{template.subject}</div>
                      </button>
                    ))}
                    <button
                      onClick={() => {
                        // TODO: Implémenter création template
                        alert('Création de template - À implémenter');
                      }}
                      className="w-full px-4 py-2 text-left text-xs text-sky-400 hover:bg-zinc-800 transition-colors flex items-center gap-2"
                    >
                      <Plus size={12} />
                      Créer un template
                    </button>
                  </div>
                </div>
              </div>
            </div>
            
            <div className="space-y-4">
              {/* DESTINATAIRES MULTIPLES */}
              <div>
                <label className="block text-[11px] uppercase text-zinc-400 mb-2">
                  Destinataires *
                </label>
                <input
                  type="text"
                  value={composeData.to.join(', ')}
                  onChange={(e) => {
                    const emails = e.target.value.split(',').map(s => s.trim()).filter(Boolean);
                    setComposeData({ ...composeData, to: emails });
                  }}
                  placeholder="email@example.com, autre@example.com..."
                  className="w-full bg-zinc-900 border border-zinc-700 rounded px-3 py-2 text-sm outline-none focus:border-sky-500"
                />
                <span className="text-[10px] text-zinc-600 mt-1 block">
                  Séparez plusieurs emails par des virgules
                </span>
              </div>

              {/* CC & BCC (optionnel, dépliable) */}
              <details className="group">
                <summary className="cursor-pointer text-xs text-zinc-500 hover:text-zinc-300 transition-colors">
                  Afficher CC / BCC
                </summary>
                <div className="mt-3 space-y-3 pl-4 border-l-2 border-zinc-800">
                  <div>
                    <label className="block text-[11px] uppercase text-zinc-400 mb-2">CC</label>
                    <input
                      type="text"
                      value={composeData.cc.join(', ')}
                      onChange={(e) => {
                        const emails = e.target.value.split(',').map(s => s.trim()).filter(Boolean);
                        setComposeData({ ...composeData, cc: emails });
                      }}
                      placeholder="Copie..."
                      className="w-full bg-zinc-900 border border-zinc-700 rounded px-3 py-2 text-sm outline-none focus:border-sky-500"
                    />
                  </div>
                  <div>
                    <label className="block text-[11px] uppercase text-zinc-400 mb-2">BCC</label>
                    <input
                      type="text"
                      value={composeData.bcc.join(', ')}
                      onChange={(e) => {
                        const emails = e.target.value.split(',').map(s => s.trim()).filter(Boolean);
                        setComposeData({ ...composeData, bcc: emails });
                      }}
                      placeholder="Copie cachée..."
                      className="w-full bg-zinc-900 border border-zinc-700 rounded px-3 py-2 text-sm outline-none focus:border-sky-500"
                    />
                  </div>
                </div>
              </details>

              {/* Sujet */}
              <div>
                <label className="block text-[11px] uppercase text-zinc-400 mb-2">Sujet *</label>
                <input
                  type="text"
                  value={composeData.subject}
                  onChange={(e) => setComposeData({ ...composeData, subject: e.target.value })}
                  placeholder="Sujet du message"
                  className="w-full bg-zinc-900 border border-zinc-700 rounded px-3 py-2 text-sm outline-none focus:border-sky-500"
                />
              </div>

              {/* OPTIONS AVANCÉES */}
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                {/* Priorité */}
                <div>
                  <label className="block text-[11px] uppercase text-zinc-400 mb-2">Priorité</label>
                  <select
                    value={composeData.priority}
                    onChange={(e) => setComposeData({ ...composeData, priority: e.target.value })}
                    className="w-full bg-zinc-900 border border-zinc-700 rounded px-3 py-2 text-sm outline-none focus:border-sky-500"
                  >
                    <option value="low">Basse</option>
                    <option value="normal">Normale</option>
                    <option value="high">Haute</option>
                    <option value="urgent">Urgente</option>
                  </select>
                </div>

                {/* Chiffrement */}
                <div>
                  <label className="block text-[11px] uppercase text-zinc-400 mb-2">Sécurité</label>
                  <label className="flex items-center gap-2 cursor-pointer px-3 py-2 bg-zinc-900 border border-zinc-700 rounded hover:bg-zinc-800 transition-colors">
                    <input
                      type="checkbox"
                      checked={composeData.encrypted}
                      onChange={(e) => setComposeData({ ...composeData, encrypted: e.target.checked })}
                      className="w-4 h-4"
                    />
                    <Lock size={14} className={composeData.encrypted ? 'text-emerald-400' : 'text-zinc-500'} />
                    <span className="text-sm text-zinc-300">Chiffrer E2E</span>
                  </label>
                </div>

                {/* Accusé de lecture */}
                <div>
                  <label className="block text-[11px] uppercase text-zinc-400 mb-2">Suivi</label>
                  <label className="flex items-center gap-2 cursor-pointer px-3 py-2 bg-zinc-900 border border-zinc-700 rounded hover:bg-zinc-800 transition-colors">
                    <input
                      type="checkbox"
                      checked={composeData.requireReadReceipt}
                      onChange={(e) => setComposeData({ ...composeData, requireReadReceipt: e.target.checked })}
                      className="w-4 h-4"
                    />
                    <Eye size={14} className={composeData.requireReadReceipt ? 'text-sky-400' : 'text-zinc-500'} />
                    <span className="text-sm text-zinc-300">Accusé lecture</span>
                  </label>
                </div>
              </div>

              {/* MESSAGE PROGRAMMÉ */}
              <div>
                <label className="block text-[11px] uppercase text-zinc-400 mb-2">
                  Envoyer plus tard (optionnel)
                </label>
                <input
                  type="datetime-local"
                  value={composeData.scheduledAt || ''}
                  onChange={(e) => setComposeData({ ...composeData, scheduledAt: e.target.value })}
                  className="w-full bg-zinc-900 border border-zinc-700 rounded px-3 py-2 text-sm outline-none focus:border-sky-500 text-zinc-300"
                />
                {composeData.scheduledAt && (
                  <div className="text-xs text-sky-400 mt-1 flex items-center gap-1">
                    <Clock size={12} />
                    Message programmé pour le {format(new Date(composeData.scheduledAt), 'dd MMM yyyy à HH:mm', { locale: fr })}
                  </div>
                )}
              </div>

              {/* Corps */}
              <div>
                <label className="block text-[11px] uppercase text-zinc-400 mb-2">Message *</label>
                <textarea
                  value={composeData.body}
                  onChange={(e) => setComposeData({ ...composeData, body: e.target.value })}
                  placeholder="Votre message..."
                  rows={12}
                  className="w-full bg-zinc-900 border border-zinc-700 rounded px-3 py-2 text-sm outline-none focus:border-sky-500 resize-none font-mono"
                />
                <div className="flex justify-between items-center mt-1">
                  <span className="text-[10px] text-zinc-600">
                    {composeData.body.length} caractères
                  </span>
                  {composeData.template && (
                    <span className="text-[10px] text-purple-400">
                      Template : {templates.find(t => t.id === composeData.template)?.name}
                    </span>
                  )}
                </div>
              </div>

              {/* PIÈCES JOINTES */}
              <div>
                <label className="block text-[11px] uppercase text-zinc-400 mb-2">Pièces jointes</label>
                
                {/* Liste pièces jointes */}
                {composeData.attachments.length > 0 && (
                  <div className="mb-3 space-y-2">
                    {composeData.attachments.map((file, idx) => (
                      <div
                        key={idx}
                        className="flex items-center justify-between p-3 bg-zinc-900/50 border border-zinc-700 rounded"
                      >
                        <div className="flex items-center gap-3 flex-1 min-w-0">
                          <Paperclip size={14} className="text-zinc-500 flex-shrink-0" />
                          <div className="flex-1 min-w-0">
                            <div className="text-sm text-zinc-300 truncate">{file.name}</div>
                            <div className="text-xs text-zinc-600">
                              {(file.size / 1024).toFixed(1)} KB · {file.type || 'unknown'}
                            </div>
                          </div>
                        </div>
                        <button
                          onClick={() => handleRemoveAttachment(idx)}
                          className="p-1.5 hover:bg-red-900/20 text-red-400 rounded transition-colors flex-shrink-0"
                          title="Retirer"
                        >
                          <X size={14} />
                        </button>
                      </div>
                    ))}
                  </div>
                )}

                {/* Bouton ajouter */}
                <label className="flex items-center justify-center gap-2 px-4 py-3 bg-zinc-900 border border-zinc-700 border-dashed rounded hover:bg-zinc-800 cursor-pointer transition-colors">
                  <Upload size={14} className="text-zinc-500" />
                  <span className="text-sm text-zinc-400">
                    Ajouter des fichiers (max 10 MB chacun)
                  </span>
                  <input
                    type="file"
                    multiple
                    className="hidden"
                    onChange={handleAddAttachment}
                  />
                </label>
              </div>

              {/* RÉPONSES RAPIDES */}
              <details className="group">
                <summary className="cursor-pointer text-xs text-zinc-500 hover:text-zinc-300 transition-colors flex items-center gap-2">
                  <MessageCircle size={12} />
                  Insérer une réponse rapide
                </summary>
                <div className="mt-3 grid grid-cols-2 md:grid-cols-3 gap-2">
                  {[
                    { label: 'Merci', text: 'Merci pour votre message.' },
                    { label: 'Bien reçu', text: 'Message bien reçu, je reviens vers vous rapidement.' },
                    { label: 'Cordialement', text: '\n\nCordialement,\n[Votre nom]' },
                    { label: 'Suivi', text: 'Suite à notre échange, je vous confirme que...' },
                    { label: 'Rappel', text: 'Je me permets de revenir vers vous concernant...' },
                    { label: 'Clôture', text: 'Cette demande est désormais traitée. N\'hésitez pas si besoin.' }
                  ].map((quick, idx) => (
                    <button
                      key={idx}
                      onClick={() => {
                        setComposeData(prev => ({
                          ...prev,
                          body: prev.body + '\n\n' + quick.text
                        }));
                      }}
                      className="px-3 py-2 bg-zinc-900 border border-zinc-700 rounded text-xs text-zinc-300 hover:bg-zinc-800 hover:border-sky-600/50 transition-all"
                    >
                      {quick.label}
                    </button>
                  ))}
                </div>
              </details>

              {/* Actions */}
              <div className="flex flex-wrap justify-end gap-2 md:gap-3 pt-4 border-t border-zinc-800">
                <button
                  onClick={() => {
                    if (composeData.subject || composeData.body) {
                      if (window.confirm('Sauvegarder le brouillon avant de quitter ?')) {
                        saveDraft();
                      }
                    }
                    setView('inbox');
                    setComposeData({
                      to: [],
                      cc: [],
                      bcc: [],
                      subject: '',
                      body: '',
                      priority: 'normal',
                      attachments: [],
                      template: null,
                      scheduledAt: null,
                      requireReadReceipt: false,
                      encrypted: false
                    });
                    closeMessaging();
                  }}
                  className="px-5 py-2 bg-zinc-800 hover:bg-zinc-700 border border-zinc-600 text-zinc-300 text-sm rounded"
                >
                  Annuler
                </button>
                
                <button onClick={saveDraft} className="px-4 md:px-5 py-2 ...">
                  <Bookmark size={14} />
                  <span className="hidden sm:inline">Sauvegarder brouillon</span>
                  <span className="sm:hidden">Brouillon</span>
                </button>
                
                <button
                  onClick={handleSendMessage}
                  disabled={!composeData.to.length || !composeData.subject || !composeData.body}
                  className="px-6 py-2 bg-sky-600 hover:bg-sky-700 disabled:bg-zinc-700 disabled:cursor-not-allowed text-white text-sm font-medium rounded flex items-center gap-2"
                >
                  <Send size={14} />
                  {composeData.scheduledAt ? 'Programmer' : 'Envoyer'}
                </button>
              </div>

              {/* Info brouillon */}
              {composeData.subject || composeData.body ? (
                <div className="text-xs text-zinc-500 flex items-center gap-2">
                  <div className="w-2 h-2 bg-emerald-500 rounded-full animate-pulse" />
                  Brouillon auto-sauvegardé
                </div>
              ) : null}
            </div>
          </div>
        )}
      </div>

      {/* MODAL CONTEXTUEL DEPUIS RECIPIENT (store) */}
      {isOpen && recipient && (
        <div className="fixed inset-0 z-[200] flex items-center justify-center bg-black/80 backdrop-blur-sm">
          <div className="w-full max-w-2xl bg-zinc-900 border border-zinc-700 shadow-2xl rounded-lg p-6">
            <div className="flex items-center justify-between mb-6">
              <h3 className="text-xl font-bold text-sky-400">Message rapide</h3>
              <button
                onClick={closeMessaging}
                className="p-2 hover:bg-zinc-800 rounded transition-colors"
              >
                <X size={20} />
              </button>
            </div>

            <div className="space-y-4">
              <div>
                <label className="text-[10px] text-zinc-500 uppercase">Destinataire</label>
                <div className="text-zinc-200 font-bold">{recipient.name}</div>
                <div className="text-xs text-zinc-500">{recipient.id}</div>
              </div>

              <div>
                <label className="text-[10px] text-zinc-500 uppercase mb-2 block">Sujet</label>
                <input
                  type="text"
                  defaultValue={`Contact : ${recipient.name}`}
                  className="w-full bg-black border border-zinc-800 rounded p-3 text-sm focus:border-sky-500 outline-none"
                />
              </div>

              <div>
                <label className="text-[10px] text-zinc-500 uppercase mb-2 block">Message</label>
                <textarea 
                  className="w-full bg-black border border-zinc-800 rounded p-3 text-sm focus:border-sky-500 outline-none h-40"
                  placeholder="Votre message..."
                  onChange={(e) => setReplyBody(e.target.value)}
                />
              </div>

              <div className="flex justify-end gap-3 pt-4 border-t border-zinc-800">
                <button
                  onClick={closeMessaging}
                  className="px-4 py-2 bg-zinc-800 hover:bg-zinc-700 border border-zinc-600 text-zinc-300 text-sm rounded"
                >
                  Annuler
                </button>
                <button 
                  onClick={() => {
                    submitMessage(`Contact : ${recipient.name}`, replyBody);
                    closeMessaging();
                    showToast('Message envoyé', 'success');
                  }}
                  className="px-6 py-2 bg-sky-600 hover:bg-sky-500 text-white font-bold uppercase text-xs tracking-widest transition-all rounded"
                >
                  <Send size={14} className="inline mr-2" />
                  Envoyer
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}